import React, { memo } from "react";
import { Handle } from "react-flow-renderer";
import {
  ContentCopy,
  Delete,
  CallSplit,
} from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";

const getOperatorSymbol = (operator) => {
  switch (operator) {
    case "equals_to":
      return "=";
    case "not_equals_to":
      return "!=";
    case "contains":
      return "contém";
    case "not_contains":
      return "não contém";
    case "greater_than":
      return ">";
    case "less_than":
      return "<";
    case "greater_than_or_equals":
      return ">=";
    case "less_than_or_equals":
      return "<=";
    case "starts_with":
      return "começa com";
    case "ends_with":
      return "termina com";
    case "is_empty":
      return "está vazio";
    case "is_not_empty":
      return "não está vazio";
    default:
      return operator;
  }
};

const getHandlePosition = (index, total) => {
  const totalItems = total + 1; // +1 para o else
  const spacing = 100 / (totalItems + 1);
  return `${spacing * (index + 1)}%`;
};

const IfNode = memo(({ data, isConnectable, id }) => {
  const storageItems = useNodeStorage();
  const conditions = data?.conditions || [];

  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: "12px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        border: "2px solid #9c27b0",
        minWidth: "220px",
        position: "relative",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position="left"
        style={{
          background: "#9c27b0",
          width: "12px",
          height: "12px",
          left: "-6px",
          top: "50%",
          transform: "translateY(-50%)",
          border: "2px solid #fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
        isConnectable={isConnectable}
      />

      {/* Botões de ação */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: 8,
          top: 8,
          cursor: "pointer",
          gap: 6,
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          sx={{ width: "14px", height: "14px", color: "#9c27b0" }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          sx={{ width: "14px", height: "14px", color: "#f44336" }}
        />
      </div>

      {/* Título */}
      <div
        style={{
          color: "#9c27b0",
          fontSize: "14px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          marginBottom: "8px",
          paddingRight: "50px",
        }}
      >
        <CallSplit
          sx={{
            width: "18px",
            height: "18px",
            marginRight: "6px",
            color: "#9c27b0",
          }}
        />
        Condição IF
      </div>

      {/* Lista de condições */}
      <div
        style={{
          fontSize: "11px",
          width: "100%",
          paddingRight: "30px",
        }}
      >
        {conditions.map((condition, index) => (
          <div
            key={index}
            style={{
              padding: "6px 8px",
              backgroundColor: "#f3e5f5",
              marginBottom: "4px",
              borderRadius: "4px",
              border: "1px solid #ce93d8",
              position: "relative",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: "#7b1fa2",
                marginBottom: "2px",
              }}
            >
              Se {index + 1}:
            </div>
            <code
              style={{
                display: "block",
                color: "#333",
                fontSize: "10px",
                wordBreak: "break-all",
              }}
            >
              {condition.variable} {getOperatorSymbol(condition.operator)}{" "}
              {condition.value}
            </code>
            {/* Subcondições AND */}
            {condition.andConditions?.map((sub, subIdx) => (
              <code
                key={`and_${subIdx}`}
                style={{
                  display: "block",
                  color: "#1976d2",
                  fontSize: "10px",
                  marginTop: "2px",
                }}
              >
                AND {sub.variable} {getOperatorSymbol(sub.operator)} {sub.value}
              </code>
            ))}
            {/* Subcondições OR */}
            {condition.orConditions?.map((sub, subIdx) => (
              <code
                key={`or_${subIdx}`}
                style={{
                  display: "block",
                  color: "#ff9800",
                  fontSize: "10px",
                  marginTop: "2px",
                }}
              >
                OR {sub.variable} {getOperatorSymbol(sub.operator)} {sub.value}
              </code>
            ))}
          </div>
        ))}

        {/* Else */}
        <div
          style={{
            padding: "6px 8px",
            backgroundColor: "#ffebee",
            borderRadius: "4px",
            border: "1px solid #ef9a9a",
          }}
        >
          <code
            style={{
              display: "block",
              color: "#c62828",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            Senão (else)
          </code>
        </div>
      </div>

      {/* Handles de saída para cada condição */}
      {conditions.map((_, index) => (
        <Handle
          key={`handle_${index}`}
          type="source"
          position="right"
          id={`condition_${index}`}
          style={{
            background: "#9c27b0",
            width: "12px",
            height: "12px",
            right: "-6px",
            top: getHandlePosition(index, conditions.length),
            border: "2px solid #fff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
          isConnectable={isConnectable}
        />
      ))}

      {/* Handle do else */}
      <Handle
        type="source"
        position="right"
        id="else"
        style={{
          background: "#f44336",
          width: "12px",
          height: "12px",
          right: "-6px",
          top: getHandlePosition(conditions.length, conditions.length),
          border: "2px solid #fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
        isConnectable={isConnectable}
      />
    </div>
  );
});

export default IfNode;
